import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';

import { useFeatureFlags } from 'components/shared/feature-flags';

import { trackMarkContextIconClicked } from '../analytics/mark-panel-analytics';

import { useMarkPanelActions } from './mark-panel-context';
import type {
  MarkContext,
  MarkContextTarget,
  MarkPageContext,
  MarkSurfaceId,
} from './mark-panel.types';

const resolveContextItemId = (
  target: MarkContextTarget
): string | undefined => {
  switch (target.kind) {
    case 'tile':
      return target.tileId;
    case 'entity':
      return target.entityId;
    default:
      return undefined;
  }
};

export interface MarkSurfaceContextValue {
  requestOpen: (
    target: MarkContextTarget,
    triggerEl?: HTMLElement | null
  ) => void;
}

const MarkSurfaceContext = createContext<MarkSurfaceContextValue | null>(null);

export const useMarkSurface = (): MarkSurfaceContextValue | null =>
  useContext(MarkSurfaceContext);

export interface MarkSurfaceProviderProps<TSnapshot> {
  surfaceId: MarkSurfaceId;
  clientId: string;
  isEnabled: boolean;
  isAnalyticsEnabled?: boolean;
  snapshot: TSnapshot;
  resolveContext: (
    target: MarkContextTarget,
    snapshot: TSnapshot
  ) => MarkContext | null;
  buildPageContext: (
    context: MarkContext,
    snapshot: TSnapshot
  ) => MarkPageContext | null;
  children: ReactNode;
}

export const MarkSurfaceProvider = <TSnapshot,>({
  surfaceId,
  clientId,
  isEnabled,
  isAnalyticsEnabled = true,
  snapshot,
  resolveContext,
  buildPageContext,
  children,
}: MarkSurfaceProviderProps<TSnapshot>) => {
  const { featureFlags } = useFeatureFlags();
  const actions = useMarkPanelActions();
  const isFlagEnabled = featureFlags['ai-assistant'];

  const snapshotRef = useRef(snapshot);

  snapshotRef.current = snapshot;

  const resolveContextRef = useRef(resolveContext);

  resolveContextRef.current = resolveContext;

  const buildPageContextRef = useRef(buildPageContext);

  buildPageContextRef.current = buildPageContext;

  useEffect(() => {
    if (!isEnabled || !isFlagEnabled || !actions) return undefined;

    return actions.registerSurface({
      id: surfaceId,
      clientId,
      isAnalyticsEnabled,
      resolvePageContext: () =>
        resolveContextRef.current({ kind: 'page' }, snapshotRef.current),
      buildPageContext: (context) =>
        buildPageContextRef.current(context, snapshotRef.current),
    });
  }, [
    actions,
    clientId,
    isAnalyticsEnabled,
    isEnabled,
    isFlagEnabled,
    surfaceId,
  ]);

  const value = useMemo<MarkSurfaceContextValue | null>(() => {
    if (!isEnabled || !isFlagEnabled || !actions) return null;

    return {
      requestOpen: (target, triggerEl) => {
        const context = resolveContextRef.current(target, snapshotRef.current);

        if (!context) return;

        if (isAnalyticsEnabled) {
          trackMarkContextIconClicked({
            surface: context.surface,
            contextKind: context.kind,
            contextSection: context.section,
            contextItemId: resolveContextItemId(target),
          });
        }

        actions.openWithContext({
          context,
          entryPoint: 'contextIcon',
          anchorRect: triggerEl?.getBoundingClientRect(),
          anchorElement: triggerEl ?? null,
        });
      },
    };
  }, [actions, isAnalyticsEnabled, isEnabled, isFlagEnabled]);

  return (
    <MarkSurfaceContext.Provider value={value}>
      {children}
    </MarkSurfaceContext.Provider>
  );
};
